import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
// Importar desde la ruta correcta en config/
import { db, storage } from '../../../config/firebase';

/**
 * Función auxiliar para subir a Google Cloud Storage
 * Carpeta: 'postulation'
 */
const uploadCV = async (file) => {
  if (!file) return null;

  try {
    // 1. Crear referencia al archivo
    // Usamos timestamp para evitar colisiones de nombres
    const fileName = `${Date.now()}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
    const storageRef = ref(storage, `postulation/${fileName}`);

    // 2. Subir bytes (con metadata básica)
    const metadata = { contentType: file.type };
    const snapshot = await uploadBytes(storageRef, file, metadata);

    // 3. Obtener URL pública
    const downloadURL = await getDownloadURL(snapshot.ref);

    return {
      url: downloadURL,
      path: snapshot.metadata.fullPath,
      originalName: file.name,
    };
  } catch (error) {
    // Log detallado para debugging
    console.error('GCS Upload Error:', {
      code: error.code,
      message: error.message,
      serverResponse: error.serverResponse,
    });

    // Mensaje descriptivo para el usuario
    let userMessage = 'Error al subir el archivo. ';
    if (error.code === 'storage/unauthorized') {
      userMessage += 'No tienes permisos para subir archivos.';
    } else if (error.code === 'storage/quota-exceeded') {
      userMessage += 'Se ha excedido la cuota de almacenamiento.';
    } else if (error.code === 'storage/unknown') {
      userMessage += 'Error de conexión, por favor verifica tu internet.';
    } else {
      userMessage += 'Por favor intenta nuevamente.';
    }

    throw new Error(userMessage);
  }
};

/**
 * Función principal para guardar en Firestore
 * Colección: 'job_applications'
 */
export const savePostulationToFirebase = async (generalData, resumeData) => {
  try {
    // Paso 1: Intentar subir el archivo
    let uploadedFile = null;
    if (resumeData.file) {
      // Validamos tamaño antes de intentar subir (Max 5MB)
      if (resumeData.file.size > 5 * 1024 * 1024) {
        throw new Error('El archivo supera el límite de 5MB.');
      }
      uploadedFile = await uploadCV(resumeData.file);
    }

    // Paso 2: Construir objeto en INGLÉS para la colección 'job_applications'
    const applicationPayload = {
      // Personal Info
      personalInformation: {
        firstName: generalData.nombres,
        lastName: generalData.apellidos,
        birthDate: generalData.fecha,
        gender: generalData.genero,
        email: generalData.email,
        phone: generalData.telefono,
        location: {
          department: generalData.departamento,
          municipality: generalData.municipio,
        },
        professionalProfile: {
          profession: generalData.profesion,
          occupation: generalData.oficio,
          summary: resumeData.aboutMe || '',
        },
      },

      // Skills
      skills: generalData.habilidades.map((h) => (typeof h === 'string' ? h : h.label)),

      // Resume File Data
      resume: uploadedFile
        ? {
            url: uploadedFile.url,
            storagePath: uploadedFile.path,
            fileName: uploadedFile.originalName,
          }
        : null,

      // Work Experience
      workExperience: resumeData.experiences.map((exp) => ({
        jobTitle: exp.cargo,
        company: exp.empresa,
        employmentType: exp.tipoEmpleo,
        workModality: exp.modalidad,
        isCurrentRole: exp.actualmente,
        startDate: exp.fechaInicio,
        endDate: exp.actualmente ? null : exp.fechaFin,
        description: exp.descripcion || '',
      })),

      // Education
      education: resumeData.education.map((edu) => ({
        institution: edu.institucion,
        degree: edu.titulo,
        fieldOfStudy: edu.disciplina || '',
        startDate: edu.fechaInicio,
        endDate: edu.fechaFin,
        description: edu.descripcion || '',
      })),

      // Metadata
      metadata: {
        createdAt: serverTimestamp(),
        status: 'received',
        source: 'web_form',
      },
    };

    // Paso 3: Guardar en Firestore
    const docRef = await addDoc(collection(db, 'workWithUs'), applicationPayload);

    console.log('Application saved ID:', docRef.id);
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Save Process Failed:', error);
    // Retornamos el error para mostrar alerta en UI
    return { success: false, error: error.message || 'Error desconocido' };
  }
};
